import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { Note } from "../../types";
import { CreateNoteResponse, UpdateNoteResponse } from "../../types/responses";
import { generateUUID } from "../../utils/uuidUtils";
import { API_ENDPOINTS, authenticatedFetch } from "../../config/api";

interface NotesState {
  items: Note[];
  loading: boolean;
  error: string | null;
}

// Async thunk for fetching notes via API
const fetchNotes = createAsyncThunk(
  "notes/fetchNotes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.NOTES.GET_ALL);

      if (!response.ok) {
        throw new Error(`Failed to fetch notes: ${response.statusText}`);
      }

      const notes: Note[] = await response.json();
      return notes;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for creating a note
const createNote = createAsyncThunk(
  "notes/createNote",
  async (noteData: Omit<Note, "id" | "createdAt" | "updatedAt" | "URLfriendlyname">, { rejectWithValue }) => {
    try {
      const { userId, ...apiData } = noteData; // Exclude userId from API payload
      const response = await authenticatedFetch(API_ENDPOINTS.NOTES.CREATE, {
        method: 'POST',
        body: JSON.stringify(apiData),
      });

      if (!response.ok) {
        throw new Error(`Failed to create note: ${response.statusText}`);
      }

      const result: CreateNoteResponse = await response.json();
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for fetching a public note without authentication
const fetchPublicNote = createAsyncThunk(
  "notes/fetchPublicNote",
  async (noteId: string, { rejectWithValue }) => {
    try {
      const response = await fetch(API_ENDPOINTS.NOTES.GET_PUBLIC(noteId));

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Note not found or is private");
        }
        throw new Error(`Failed to fetch public note: ${response.statusText}`);
      }

      const note: Note = await response.json();
      return note;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for updating a note
const updateNoteById = createAsyncThunk(
  "notes/updateNoteById",
  async ({ id, updates }: { id: string; updates: Partial<Note> }, { rejectWithValue }) => {
    try {
      // Exclude fields that shouldn't be sent to the API
      const { id: _, createdAt, updatedAt, userId, ...apiData } = updates;

      const response = await authenticatedFetch(API_ENDPOINTS.NOTES.UPDATE(id), {
        method: 'PUT',
        body: JSON.stringify(apiData),
      });

      if (!response.ok) {
        throw new Error(`Failed to update note: ${response.statusText}`);
      }

      const result: UpdateNoteResponse = await response.json();
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for deleting a note
const deleteNoteById = createAsyncThunk(
  "notes/deleteNoteById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.NOTES.DELETE(id), {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete note: ${response.statusText}`);
      }

      return id;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for fetching a note by URL-friendly name
const fetchNoteByURLFriendlyName = createAsyncThunk(
  "notes/fetchNoteByURLFriendlyName",
  async (urlfriendlyname: string, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.NOTES.GET_BY_URLFRIENDLYNAME(urlfriendlyname));

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Note not found or access denied");
        }
        throw new Error(`Failed to fetch note: ${response.statusText}`);
      }

      const note: Note = await response.json();
      return note;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for sharing a note
const shareNote = createAsyncThunk(
  "notes/shareNote",
  async (noteId: string, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.NOTES.SHARE, {
        method: 'POST',
        body: JSON.stringify({ noteId }),
      });

      if (!response.ok) {
        throw new Error(`Failed to share note: ${response.statusText}`);
      }

      // No response body expected
      return noteId;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

const initialState: NotesState = {
  items: [],
  loading: false,
  error: null,
};

const notesSlice = createSlice({
  name: "notes",
  initialState,
  reducers: {
    addNote: (
      state,
      action: PayloadAction<Omit<Note, "id" | "createdAt" | "updatedAt" | "URLfriendlyname">>
    ) => {
      const newNote: Note = {
        ...action.payload,
        id: generateUUID(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        URLfriendlyname: "", // Placeholder, should be generated by API
      };
      state.items.push(newNote);
    },
    updateNote: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<Note> }>
    ) => {
      const { id, updates } = action.payload;
      const noteIndex = state.items.findIndex((note) => note.id === id);
      if (noteIndex !== -1) {
        state.items[noteIndex] = {
          ...state.items[noteIndex],
          ...updates,
          updatedAt: new Date().toISOString(),
        };
      }
    },
    deleteNote: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((note) => note.id !== action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearNotes: (state) => {
      state.items = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotes.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchNotes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createNote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNote.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
      })
      .addCase(createNote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateNoteById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateNoteById.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(n => n.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateNoteById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteNoteById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteNoteById.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(n => n.id !== action.payload);
      })
      .addCase(deleteNoteById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchPublicNote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPublicNote.fulfilled, (state, action) => {
        state.loading = false;
        // For public notes, we don't add them to the main items array
        // They are handled separately in the component
      })
      .addCase(fetchPublicNote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchNoteByURLFriendlyName.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNoteByURLFriendlyName.fulfilled, (state, action) => {
        state.loading = false;
        // For URL-friendly notes, we don't add them to the main items array
        // They are handled separately in the component
      })
      .addCase(fetchNoteByURLFriendlyName.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { addNote, updateNote, deleteNote, setLoading, setError, clearNotes } =
  notesSlice.actions;
export { fetchNotes, createNote, updateNoteById, deleteNoteById, fetchPublicNote, fetchNoteByURLFriendlyName, shareNote };
export default notesSlice.reducer;
